import type {
  FunnelIntakeAnswers,
  FunnelIntakeQuestion,
  FunnelIntakeStep,
} from "../models";
import { FUNNEL_ROUTE, funnelIntakeRoute } from "../router/constants";

export type FunnelIntakeQuestionIndex = Map<string, FunnelIntakeQuestion>;

type Answer = FunnelIntakeAnswers[string] | undefined;

const followUpsOf = (question: FunnelIntakeQuestion) =>
  question.options.flatMap((option) => option.followUps);

const questionTree = (
  questions: FunnelIntakeQuestion[],
): FunnelIntakeQuestion[] => {
  const byKey = new Map<string, FunnelIntakeQuestion>();
  const visit = (question: FunnelIntakeQuestion) => {
    if (byKey.has(question.key)) {
      return;
    }
    byKey.set(question.key, question);
    followUpsOf(question).forEach(visit);
  };
  questions.forEach(visit);

  return [...byKey.values()];
};

const selectedValues = (answer: Answer) => {
  if (answer === undefined) {
    return [];
  }

  return Array.isArray(answer) ? answer : [answer];
};

export const indexQuestions = (
  steps: FunnelIntakeStep[],
): FunnelIntakeQuestionIndex =>
  new Map(
    questionTree(steps.flatMap((step) => step.questions)).map((question) => [
      question.key,
      question,
    ]),
  );

const offeredAnswer = (
  question: FunnelIntakeQuestion,
  answer: Answer,
): Answer => {
  if (question.options.length === 0 || answer === undefined) {
    return answer;
  }

  const isOffered = (value: string) =>
    question.options.some((option) => option.value === value);

  if (Array.isArray(answer)) {
    const offered = answer.filter(isOffered);

    return offered.length > 0 ? offered : undefined;
  }

  return isOffered(answer) ? answer : undefined;
};

const isAnswered = (question: FunnelIntakeQuestion, answer: Answer) =>
  selectedValues(offeredAnswer(question, answer)).some(
    (value) => value.trim() !== "",
  );

export const isQuestionVisible = (
  question: FunnelIntakeQuestion,
  answers: FunnelIntakeAnswers,
  index: FunnelIntakeQuestionIndex,
): boolean => {
  if (!question.visibleWhen) {
    return true;
  }

  const { questionKey, values } = question.visibleWhen;
  const parent = index.get(questionKey);

  return (
    parent !== undefined &&
    isQuestionVisible(parent, answers, index) &&
    selectedValues(answers[questionKey]).some((value) => values.includes(value))
  );
};

export const visibleQuestions = (
  step: FunnelIntakeStep,
  answers: FunnelIntakeAnswers,
  index: FunnelIntakeQuestionIndex,
) =>
  step.questions.filter((question) =>
    isQuestionVisible(question, answers, index),
  );

const visibleQuestionTree = (
  step: FunnelIntakeStep,
  answers: FunnelIntakeAnswers,
  index: FunnelIntakeQuestionIndex,
) =>
  questionTree(step.questions).filter((question) =>
    isQuestionVisible(question, answers, index),
  );

export const blockingMessages = (
  question: FunnelIntakeQuestion,
  answers: FunnelIntakeAnswers,
) => {
  const selected = selectedValues(answers[question.key]);

  const messages = question.options.flatMap((option) =>
    option.blockingMessage && selected.includes(option.value)
      ? [option.blockingMessage]
      : [],
  );

  return [...new Set(messages)];
};

export const hasBlockingMessage = (
  step: FunnelIntakeStep,
  answers: FunnelIntakeAnswers,
  index: FunnelIntakeQuestionIndex,
) =>
  visibleQuestionTree(step, answers, index).some(
    (question) => blockingMessages(question, answers).length > 0,
  );

export const isStepComplete = (
  step: FunnelIntakeStep,
  answers: FunnelIntakeAnswers,
  index: FunnelIntakeQuestionIndex,
) =>
  visibleQuestionTree(step, answers, index).every(
    (question) =>
      (!question.required || isAnswered(question, answers[question.key])) &&
      blockingMessages(question, answers).length === 0,
  );

export const hasDisqualifyingAnswer = (
  step: FunnelIntakeStep,
  answers: FunnelIntakeAnswers,
  index: FunnelIntakeQuestionIndex,
) =>
  visibleQuestionTree(step, answers, index).some((question) => {
    const selected = selectedValues(answers[question.key]);

    return question.options.some(
      (option) => option.disqualifies && selected.includes(option.value),
    );
  });

export const pruneHiddenAnswers = (
  answers: FunnelIntakeAnswers,
  index: FunnelIntakeQuestionIndex,
): FunnelIntakeAnswers => {
  const offered: FunnelIntakeAnswers = {};
  for (const [key, answer] of Object.entries(answers)) {
    const question = index.get(key);
    const value = question && offeredAnswer(question, answer);
    if (value !== undefined) {
      offered[key] = value;
    }
  }

  return Object.fromEntries(
    Object.entries(offered).filter(([key]) => {
      const question = index.get(key);

      return (
        question !== undefined && isQuestionVisible(question, offered, index)
      );
    }),
  );
};

export const toggleOption = (
  selected: readonly string[],
  value: string,
  exclusiveValues: readonly string[] = [],
) => {
  if (selected.includes(value)) {
    return selected.filter((current) => current !== value);
  }

  if (exclusiveValues.includes(value)) {
    return [value];
  }

  return [
    ...selected.filter((current) => !exclusiveValues.includes(current)),
    value,
  ];
};

const visibleSteps = (
  steps: FunnelIntakeStep[],
  answers: FunnelIntakeAnswers,
  index: FunnelIntakeQuestionIndex,
) => steps.filter((step) => visibleQuestions(step, answers, index).length > 0);

export const firstIntakeRoute = (
  steps: FunnelIntakeStep[],
  answers: FunnelIntakeAnswers,
  index: FunnelIntakeQuestionIndex,
) => {
  const [first] = visibleSteps(steps, answers, index);

  return first ? funnelIntakeRoute(first.slug) : FUNNEL_ROUTE.MEDICAL_REVIEW;
};

export const lastIntakeRoute = (
  steps: FunnelIntakeStep[],
  answers: FunnelIntakeAnswers,
  index: FunnelIntakeQuestionIndex,
) => {
  const last = visibleSteps(steps, answers, index).at(-1);

  return last ? funnelIntakeRoute(last.slug) : FUNNEL_ROUTE.PERSONAL_INFO;
};

export const nextIntakeRoute = (
  steps: FunnelIntakeStep[],
  slug: string,
  answers: FunnelIntakeAnswers,
  index: FunnelIntakeQuestionIndex,
) => {
  const current = steps.find((step) => step.slug === slug);
  const next = visibleSteps(steps, answers, index).find(
    (step) => current !== undefined && step.order > current.order,
  );

  return next ? funnelIntakeRoute(next.slug) : FUNNEL_ROUTE.MEDICAL_REVIEW;
};

export const previousIntakeRoute = (
  steps: FunnelIntakeStep[],
  slug: string,
  answers: FunnelIntakeAnswers,
  index: FunnelIntakeQuestionIndex,
) => {
  const current = steps.find((step) => step.slug === slug);
  const previous = visibleSteps(steps, answers, index)
    .filter((step) => current !== undefined && step.order < current.order)
    .at(-1);

  return previous
    ? funnelIntakeRoute(previous.slug)
    : FUNNEL_ROUTE.PERSONAL_INFO;
};
